import { ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import { InlineMarkdown } from '@/components/core/markdown/markdown';
import type { OpenableSection } from '@/lib/lesson/textSections';
import { ReadCheckbox } from './ReadCheckbox';

/** Concept names shown on a row before "+N". */
const CONCEPTS_SHOWN = 4;

/**
 * The lesson text's contents: one row per section of the plan - its number,
 * title, goal and concepts, the way the build view showed the plan - each
 * opening that section on its own. The student marks a section read with
 * its checkbox (here or at the end of the section); one still being written
 * says so and cannot be opened yet.
 */
export function TextContents({
  sections,
  read,
  onOpen,
  onRead,
}: {
  sections: OpenableSection[];
  read: Set<number>;
  onOpen: (index: number) => void;
  onRead: (index: number, read: boolean) => void;
}) {
  return (
    <nav aria-label='Lesson sections' className='mt-8'>
      <h2 className='mb-3 font-mono text-xs uppercase tracking-wider text-muted-foreground/90'>
        {sections.length} sections
      </h2>
      <ol className='space-y-2'>
        {sections.map(section => (
          <li key={section.index} className='relative'>
            <SectionButton
              section={section}
              read={read.has(section.index)}
              onOpen={() => onOpen(section.index)}
            />
            {/* Beside the row's button, not inside it (a button in a
                button is invalid): top-right, over the row's padding. */}
            <ReadCheckbox
              checked={read.has(section.index)}
              onChange={checked => onRead(section.index, checked)}
              disabled={!section.ready}
              label={`Mark section ${section.index} as read`}
              className='absolute right-4 top-4'
            />
          </li>
        ))}
      </ol>
    </nav>
  );
}

function SectionButton({
  section,
  read,
  onOpen,
}: {
  section: OpenableSection;
  read: boolean;
  onOpen: () => void;
}) {
  return (
    <button
      type='button'
      onClick={onOpen}
      disabled={!section.ready}
      aria-label={`Section ${section.index}: ${section.title}${
        section.ready ? (read ? ', read' : '') : ', still being written'
      }`}
      className={cn(
        'group flex w-full gap-3 rounded-xl border border-foreground/10 p-4 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        section.ready
          ? 'hover:border-brand-500/40 hover:bg-brand-500/5 dark:hover:border-primary/40 dark:hover:bg-primary/5'
          : 'cursor-default opacity-70'
      )}
    >
      <span
        className='mt-px flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-brand-500/40 bg-brand-500/10 font-mono text-xs font-medium text-foreground dark:border-primary/40 dark:bg-primary/15'
        aria-hidden
      >
        {section.index}
      </span>
      <span className='min-w-0 flex-1'>
        <InlineMarkdown className='block font-semibold text-foreground'>
          {section.title}
        </InlineMarkdown>
        {section.goal && (
          <InlineMarkdown className='mt-0.5 block text-sm leading-relaxed text-muted-foreground'>
            {section.goal}
          </InlineMarkdown>
        )}
        <ConceptChips names={section.concepts} />
      </span>
      {/* Room for the read checkbox above; the chevron sits below it. */}
      <span
        className='mt-9 shrink-0 self-start text-muted-foreground'
        aria-hidden
      >
        {section.ready ? (
          <ChevronRight className='h-4 w-4 transition-transform group-hover:translate-x-0.5' />
        ) : (
          <span className='flex items-center gap-1.5 text-xs'>
            <Loader2 className='h-3.5 w-3.5 animate-spin motion-reduce:animate-none' />
            Writing…
          </span>
        )}
      </span>
    </button>
  );
}

function ConceptChips({ names }: { names: string[] }) {
  if (names.length === 0) return null;
  const hidden = names.length - CONCEPTS_SHOWN;
  return (
    <span className='mt-2 flex flex-wrap gap-1.5'>
      {names.slice(0, CONCEPTS_SHOWN).map(name => (
        <span
          key={name}
          className='rounded-md bg-muted/70 px-2 py-0.5 text-xs text-foreground/80'
        >
          <InlineMarkdown>{name}</InlineMarkdown>
        </span>
      ))}
      {hidden > 0 && (
        <span className='px-1 py-0.5 font-mono text-xs text-muted-foreground'>
          +{hidden}
        </span>
      )}
    </span>
  );
}
